import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../../shared/molecules/button/button.component';
import {
  ButtonTypeEnum,
  ButtonSizeEnum
} from '../../../shared/molecules/button/buttonTypesEnum';
import { ReceivedOrder } from '../../../../domain/model/ingested-order/ingested-order.model';
import { FinalSaveResponse } from '../../../../infrastructure/services/received-orders/services/received-orders.service';

export enum FinalSaveModalState {
  CONFIRMATION = 'confirmation',
  PROCESSING = 'processing',
  SUCCESS = 'success'
}

@Component({
  selector: 'app-final-save-modal',
  standalone: true,
  imports: [CommonModule, ButtonComponent],
  templateUrl: './final-save-modal.component.html',
  styleUrls: ['./final-save-modal.component.scss']
})
export class FinalSaveModalComponent {
  isOpen = input.required<boolean>();
  state = input<FinalSaveModalState>(FinalSaveModalState.CONFIRMATION);
  selectedCount = input.required<number>();
  selectedSamples = input<ReceivedOrder[]>([]);
  response = input<FinalSaveResponse | null>(null);

  confirm = output();
  closeModal = output();

  readonly FinalSaveModalState = FinalSaveModalState;
  readonly ButtonTypeEnum = ButtonTypeEnum;
  readonly ButtonSizeEnum = ButtonSizeEnum;

  get modalTitle(): string {
    switch (this.state()) {
      case FinalSaveModalState.CONFIRMATION:
        return 'Confirmar Guardado Definitivo';
      case FinalSaveModalState.PROCESSING:
        return 'Procesando...';
      case FinalSaveModalState.SUCCESS:
        return 'Guardado Definitivo Exitoso';
      default:
        return 'Guardar Definitivo';
    }
  }

  get modalMessage(): string {
    const currentState = this.state();

    switch (currentState) {
      case FinalSaveModalState.CONFIRMATION:
        return this.getConfirmationMessage();
      case FinalSaveModalState.PROCESSING:
        return 'Guardando muestras...';
      case FinalSaveModalState.SUCCESS:
        return this.getSuccessMessage();
      default:
        return '';
    }
  }

  private getConfirmationMessage(): string {
    const count = this.selectedCount();
    const pluralSuffix = count === 1 ? '' : 's';
    return `¿Está seguro de enviar ${count} muestra${pluralSuffix} al proceso de extracción?`;
  }

  private getSuccessMessage(): string {
    const resp = this.response();
    if (!resp) {
      return 'Operación completada exitosamente';
    }

    const specimenCount = resp.specimenCount;
    const isPlural = specimenCount !== 1;
    const nounSuffix = isPlural ? 's han' : ' ha';
    const adjectiveSuffix = isPlural ? 's' : '';

    return `${specimenCount} muestra${nounSuffix} sido enviada${adjectiveSuffix} exitosamente al proceso de extracción`;
  }

  get showButtons(): boolean {
    return this.state() === FinalSaveModalState.CONFIRMATION;
  }

  get showCloseButton(): boolean {
    return this.state() !== FinalSaveModalState.PROCESSING;
  }

  get showSuccessDetails(): boolean {
    return (
      this.state() === FinalSaveModalState.SUCCESS && this.response() !== null
    );
  }

  private readonly DECIMAL_SEPARATOR_INDEX = 0;
  private readonly DATE_PADDING_LENGTH = 2;
  private readonly PADDING_CHAR = '0';

  get formattedTimestamp(): string {
    const resp = this.response();

    if (!resp?.timestamp) {
      return '';
    }

    const timestamp = resp.timestamp.split('.')[this.DECIMAL_SEPARATOR_INDEX];
    const date = new Date(timestamp);

    if (Number.isNaN(date.getTime())) {
      return resp.timestamp;
    }

    return this.formatDateComponents(date);
  }

  private formatDateComponents(date: Date): string {
    const day = this.padNumber(date.getDate());
    const month = this.padNumber(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = this.padNumber(date.getHours());
    const minutes = this.padNumber(date.getMinutes());
    const seconds = this.padNumber(date.getSeconds());

    return `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
  }

  private padNumber(num: number): string {
    return num.toString().padStart(this.DATE_PADDING_LENGTH, this.PADDING_CHAR);
  }

  onConfirm(): void {
    this.confirm.emit();
  }

  onCancel(): void {
    this.closeModal.emit();
  }

  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget && this.showCloseButton) {
      this.onCancel();
    }
  }
}
